// Pagination engine.
//
// StPageFlip needs an array of fixed-size page elements, but our content is
// reflowable prose + images. This module measures content into a page-sized box
// and distributes block elements across pages, splitting long paragraphs at word
// boundaries so text flows naturally from one page to the next.

export interface PageSize {
  width: number;
  height: number;
}

// Break a block-level text element into word-level pieces that preserve inline
// markup (each marked span stays intact; bare text splits per word).
function wordPieces(source: HTMLElement): Node[] {
  const pieces: Node[] = [];
  source.childNodes.forEach((child) => {
    if (child.nodeType === Node.TEXT_NODE) {
      const words = (child.textContent ?? "").split(/(\s+)/).filter((w) => w.length);
      for (const w of words) pieces.push(document.createTextNode(w));
    } else {
      pieces.push(child.cloneNode(true));
    }
  });
  return pieces;
}

const SPLITTABLE = new Set(["P", "BLOCKQUOTE"]);

export function paginate(blocks: HTMLElement[], size: PageSize): HTMLElement[] {
  // Offscreen measuring host sized like a real page.
  const host = document.createElement("div");
  host.style.cssText = `position:fixed;left:-10000px;top:0;width:${size.width}px;height:${size.height}px;`;
  const measure = document.createElement("div");
  measure.className = "page-inner";
  host.appendChild(measure);
  document.body.appendChild(host);

  const available = () => measure.clientHeight;
  const overflowing = () => measure.scrollHeight > available() + 1;

  const pages: HTMLElement[] = [];

  const finalize = () => {
    pages.push(snapshot(measure));
    measure.replaceChildren();
  };

  for (const block of blocks) {
    measure.appendChild(block);
    if (!overflowing()) continue;

    // Doesn't fit. If the page already has content, try it fresh on a new page.
    if (measure.childElementCount > 1) {
      measure.removeChild(block);
      finalize();
      measure.appendChild(block);
      if (!overflowing()) continue;
    }

    // Still overflowing as the only block. Split it if it's text; else accept.
    if (SPLITTABLE.has(block.tagName)) {
      measure.removeChild(block);
      splitInto(block, measure, finalize, overflowing);
    }
    // images/lists that overflow alone: leave them (rare); they scale via CSS.
  }

  if (measure.childNodes.length) finalize();
  document.body.removeChild(host);

  if (pages.length === 0) pages.push(makeInner());
  return pages;

  // ---- helpers bound to this run ----
  function makeInner(): HTMLElement {
    const d = document.createElement("div");
    d.className = "page-inner";
    return d;
  }
  function snapshot(el: HTMLElement): HTMLElement {
    const clone = el.cloneNode(true) as HTMLElement;
    return clone;
  }
}

// Pour a splittable text block word-by-word across pages.
function splitInto(
  block: HTMLElement,
  measure: HTMLElement,
  finalize: () => void,
  overflowing: () => boolean,
) {
  const pieces = wordPieces(block);
  let para = freshPara(block);
  measure.appendChild(para);

  for (const piece of pieces) {
    para.appendChild(piece);
    if (overflowing()) {
      para.removeChild(piece);
      // close this page; start a continuation paragraph on the next page
      if (para.childNodes.length === 0) {
        // even one word overflows an empty page — force-place it to avoid a loop
        para.appendChild(piece);
        finalize();
        para = freshPara(block);
        measure.appendChild(para);
        continue;
      }
      finalize();
      para = freshPara(block);
      measure.appendChild(para);
      para.appendChild(piece);
    }
  }
}

// A continuation paragraph keeps the tag/classes but drops first-letter drop-caps.
function freshPara(source: HTMLElement): HTMLElement {
  const p = document.createElement(source.tagName.toLowerCase());
  p.className = source.className.replace("drop", "").trim();
  return p;
}
